import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Mail,
  Plus,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Save,
  X,
  Users,
  Eye,
  EyeOff,
  ChevronRight,
  RefreshCw,
  Send,
} from 'lucide-react';
import { api } from '../services/api';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const SMTP_PRESETS = {
  gmail: { label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 587, useSsl: false },
  outlook: { label: 'Outlook / Office 365', host: 'smtp.office365.com', port: 587, useSsl: false },
  yahoo: { label: 'Yahoo Mail', host: 'smtp.mail.yahoo.com', port: 587, useSsl: false },
  custom: { label: 'Custom SMTP server', host: '', port: 587, useSsl: false },
};

const defaultFormData = {
  host: '',
  port: 587,
  username: '',
  password: '',
  fromName: '',
  fromEmail: '',
  useSsl: false,
};

const EmailConfigManagement = () => {
  const [stores, setStores] = useState([]);
  const [enterprises, setEnterprises] = useState([]);
  const [bocs, setBocs] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [reps, setReps] = useState([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState('');
  const [selectedBocId, setSelectedBocId] = useState('');
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const errorRef = useRef(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [preset, setPreset] = useState('custom');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [testEmailDialog, setTestEmailDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailError, setTestEmailError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [storesRes, configsRes, repsRes, enterprisesRes, bocsRes] = await Promise.all([
        api.get('/admin/stores'),
        api.get('/admin/email-config/all'),
        api.get('/admin/sales-reps/all'),
        api.get('/admin/enterprises'),
        api.get('/admin/bocs'),
      ]);
      setStores(storesRes.data);
      setConfigs(configsRes.data);
      setReps(repsRes.data);
      setEnterprises(enterprisesRes.data);
      setBocs(bocsRes.data);
    } catch (err) {
      console.error('Error loading email config overview:', err);
      setError('Failed to load stores and email configurations');
    } finally {
      setLoading(false);
    }
  };

  const storeLabel = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    return store ? (store.name || store.storeId) : `Store #${storeId}`;
  };

  const configFor = (storeId) => configs.find((c) => c.storeIdFk === storeId) || null;
  const repCountFor = (storeId) => reps.filter((r) => r.storeIdFk === storeId).length;

  const bocsForSelectedEnterprise = selectedEnterpriseId
    ? bocs.filter((b) => b.enterpriseIdFk === selectedEnterpriseId)
    : [];

  const filteredStores = stores.filter((s) => {
    if (selectedEnterpriseId && s.enterpriseIdFk !== selectedEnterpriseId) return false;
    if (selectedBocId && s.bocIdFk !== selectedBocId) return false;
    return true;
  });

  const handleEnterpriseChange = (value) => {
    setSelectedEnterpriseId(value);
    setSelectedBocId('');
    setSelectedStoreId(null);
  };

  const handleBocChange = (value) => {
    setSelectedBocId(value);
    setSelectedStoreId(null);
  };

  const readiness = (storeId) => {
    const hasConfig = !!configFor(storeId);
    const repCount = repCountFor(storeId);
    if (hasConfig && repCount > 0) return { key: 'ready', label: 'Ready', classes: 'bg-green-100 text-green-800' };
    if (hasConfig && repCount === 0) return { key: 'noreps', label: 'No sales reps', classes: 'bg-amber-100 text-amber-800' };
    if (!hasConfig && repCount > 0) return { key: 'noconfig', label: 'No email config', classes: 'bg-red-100 text-red-700' };
    return { key: 'unset', label: 'Not set up', classes: 'bg-gray-100 text-gray-600' };
  };

  const summary = useMemo(() => {
    const total = stores.length;
    const ready = stores.filter((s) => readiness(s.id).key === 'ready').length;
    return { total, ready };
  }, [stores, configs, reps]);

  const selectedConfig = selectedStoreId != null ? configFor(selectedStoreId) : null;
  const selectedRepCount = selectedStoreId != null ? repCountFor(selectedStoreId) : 0;

  const applyPreset = (key) => {
    setPreset(key);
    const p = SMTP_PRESETS[key];
    setFormData((prev) => ({
      ...prev,
      host: p.host || prev.host,
      port: p.port,
      useSsl: p.useSsl,
    }));
  };

  const handleSecurityChange = (useSsl) => {
    setFormData((prev) => ({
      ...prev,
      useSsl,
      // Only auto-swap between the two standard ports; leave custom ports alone
      port: prev.port === 587 || prev.port === 465 ? (useSsl ? 465 : 587) : prev.port,
    }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.host.trim()) errs.host = 'Host is required';
    if (!formData.port || formData.port < 1 || formData.port > 65535) errs.port = 'Port must be between 1 and 65535';
    if (!formData.username.trim()) errs.username = 'Username is required';
    if (!selectedConfig && !formData.password) errs.password = 'Password is required for a new config';
    if (formData.fromEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail)) errs.fromEmail = 'Invalid email address';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = { ...formData, storeIdFk: selectedStoreId };
      const response = selectedConfig
        ? await api.put(`/admin/email-config/${selectedConfig.id}`, payload)
        : await api.post('/admin/email-config', payload);
      setConfigs((prev) => {
        const rest = prev.filter((c) => c.storeIdFk !== selectedStoreId);
        return [...rest, response.data];
      });
      setSuccess('Email configuration saved');
      setTimeout(() => setSuccess(''), 2500);
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving email config:', err);
      setError(err.response?.data?.message || 'Failed to save email configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = () => {
    setError('');
    setFormData({
      host: selectedConfig?.host || '',
      port: selectedConfig?.port || 587,
      username: selectedConfig?.username || '',
      password: '',
      fromName: selectedConfig?.fromName || '',
      fromEmail: selectedConfig?.fromEmail || '',
      useSsl: selectedConfig?.useSsl || false,
    });
    const matched = Object.entries(SMTP_PRESETS).find(([, p]) => p.host && p.host === selectedConfig?.host);
    setPreset(matched ? matched[0] : 'custom');
    setFormErrors({});
    setShowPassword(false);
    setOpenDialog(true);
  };

  const handleDeactivate = async () => {
    if (!selectedConfig) return;
    const confirmed = await confirm({
      title: `Deactivate email config for ${storeLabel(selectedStoreId)}?`,
      message: 'Handoff emails for this store will stop until a new config is saved.',
      confirmLabel: 'Deactivate',
    });
    if (!confirmed) return;
    setError('');
    try {
      await api.delete(`/admin/email-config/${selectedConfig.id}`);
      setConfigs((prev) => prev.filter((c) => c.id !== selectedConfig.id));
      setSuccess('Email configuration deactivated');
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      console.error('Error deactivating email config:', err);
      setError(err.response?.data?.message || 'Failed to deactivate email configuration');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setTestEmailError('Please enter an email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      setTestEmailError('Please enter a valid email address');
      return;
    }
    setSendingTestEmail(true);
    setTestEmailError('');
    try {
      await api.post(`/admin/email-config/${selectedConfig.id}/test`, null, {
        params: { toEmail: testEmail },
      });
      setSuccess(`Test email sent successfully to ${testEmail}`);
      setTestEmailDialog(false);
      setTestEmail('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestEmailError(err.response?.data?.message || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // The dialog can be taller than the viewport; bring a save error into view so it is never off-screen.
  useEffect(() => {
    if (error && openDialog) {
      errorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }
  }, [error, openDialog]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError('');
    setFormData(defaultFormData);
    setFormErrors({});
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
      formErrors[field] ? 'border-red-400' : 'border-gray-300'
    }`;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Mail}
        title="SMTP Config"
        subtitle="Configure the SMTP account used to send lead handoff emails"
        rightContent={
          <>
            {!loading && (
              <div className="text-right text-sm text-blue-100">
                <span className="font-semibold text-white">{summary.ready}</span> of{' '}
                <span className="font-semibold text-white">{summary.total}</span> stores ready
              </div>
            )}
            <button
              onClick={loadAll}
              title="Refresh"
              className="p-2.5 rounded-xl text-white bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </>
        }
      />

      {success && (
        <div className="rounded-xl bg-green-50 border border-green-200 p-4 shadow-sm flex">
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
          <p className="ml-3 text-sm font-medium text-green-800">{success}</p>
        </div>
      )}
      {error && !openDialog && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm flex">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
        </div>
      )}

      {loading ? (
        <AdminLoading message="Loading email configurations..." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Store overview list */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden self-start">
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
              <h3 className="text-base font-semibold text-gray-900">Stores</h3>
              <p className="text-xs text-gray-500 mt-0.5">Handoff readiness per store</p>
            </div>
            <div className="px-5 py-4 border-b border-gray-200 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Enterprise</label>
                <select
                  value={selectedEnterpriseId}
                  onChange={(e) => handleEnterpriseChange(e.target.value ? Number(e.target.value) : '')}
                  className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Enterprises</option>
                  {enterprises.map((ent) => (
                    <option key={ent.id} value={ent.id}>{ent.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">BOC</label>
                <select
                  value={selectedBocId}
                  onChange={(e) => handleBocChange(e.target.value ? Number(e.target.value) : '')}
                  disabled={!selectedEnterpriseId}
                  className="w-full text-sm px-2.5 py-1.5 border border-gray-300 rounded-md bg-white focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <option value="">All BOCs</option>
                  {bocsForSelectedEnterprise.map((boc) => (
                    <option key={boc.id} value={boc.id}>{boc.name || boc.bocId}</option>
                  ))}
                </select>
              </div>
            </div>
            {filteredStores.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No stores found.</div>
            ) : (
              <ul className="divide-y divide-gray-100 max-h-[32rem] overflow-y-auto">
                {filteredStores.map((store) => {
                  const status = readiness(store.id);
                  const count = repCountFor(store.id);
                  const isSelected = selectedStoreId === store.id;
                  return (
                    <li key={store.id}>
                      <button
                        onClick={() => setSelectedStoreId(store.id)}
                        className={`w-full text-left px-5 py-3.5 flex items-center justify-between transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{store.name || store.storeId}</p>
                          <p className="text-xs text-gray-500 flex items-center mt-0.5">
                            <span className="font-mono">{store.storeId}</span>
                            <span className="mx-1.5">·</span>
                            <Users className="h-3 w-3 mr-1" />
                            {count} rep{count === 1 ? '' : 's'}
                          </p>
                        </div>
                        <div className="flex items-center flex-shrink-0 ml-3">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${status.classes}`}>
                            {status.label}
                          </span>
                          <ChevronRight className="h-4 w-4 text-gray-300 ml-2" />
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-3">
            {selectedStoreId == null ? (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center">
                <Mail className="h-12 w-12 mb-3 text-gray-300" />
                <p className="text-sm">Select a store on the left to view or edit its email configuration.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Readiness warnings */}
                {selectedConfig && selectedRepCount === 0 && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 shadow-sm flex">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div className="ml-3 text-sm text-amber-800">
                      <p className="font-semibold">Handoff emails will NOT be sent for this store.</p>
                      <p className="mt-0.5">SMTP is configured, but there are no active sales reps to receive the emails. Add reps on the Sales Representatives page.</p>
                    </div>
                  </div>
                )}
                {!selectedConfig && selectedRepCount > 0 && (
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 shadow-sm flex">
                    <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />
                    <div className="ml-3 text-sm text-amber-800">
                      <p className="font-semibold">Handoff emails will NOT be sent for this store.</p>
                      <p className="mt-0.5">{selectedRepCount} sales rep{selectedRepCount === 1 ? ' is' : 's are'} set up, but no SMTP config exists yet. Add one below.</p>
                    </div>
                  </div>
                )}

                {selectedConfig ? (
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <Mail className="h-5 w-5 mr-2 text-gray-600" />
                        {storeLabel(selectedStoreId)} SMTP
                      </h3>
                      <AdminRowActions>
                        <AdminActionButton variant="info" icon={Send} label="Test Email" onClick={() => { setTestEmailDialog(true); setTestEmailError(''); }} />
                        <AdminActionButton variant="edit" label="Edit Config" onClick={handleOpenEdit} />
                        <AdminActionButton variant="deactivate" onClick={handleDeactivate} />
                      </AdminRowActions>
                    </div>
                    <dl className="divide-y divide-gray-200">
                      {[
                        ['SMTP Host', selectedConfig.host],
                        ['Port', selectedConfig.port],
                        ['Username', selectedConfig.username],
                        ['Password', '••••••••'],
                        ['From Name', selectedConfig.fromName || '—'],
                        ['From Email', selectedConfig.fromEmail || '—'],
                      ].map(([label, value]) => (
                        <div key={label} className="px-6 py-3.5 grid grid-cols-3 gap-4">
                          <dt className="text-sm font-medium text-gray-500">{label}</dt>
                          <dd className="col-span-2 text-sm text-gray-900">{value}</dd>
                        </div>
                      ))}
                      <div className="px-6 py-3.5 grid grid-cols-3 gap-4">
                        <dt className="text-sm font-medium text-gray-500">Security</dt>
                        <dd className="col-span-2 text-sm">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                            {selectedConfig.useSsl ? 'SSL/TLS' : 'STARTTLS'}
                          </span>
                        </dd>
                      </div>
                      <div className="px-6 py-3.5 grid grid-cols-3 gap-4">
                        <dt className="text-sm font-medium text-gray-500">Recipients</dt>
                        <dd className="col-span-2 text-sm text-gray-900 flex items-center">
                          <Users className="h-4 w-4 mr-1.5 text-gray-400" />
                          {selectedRepCount} active sales rep{selectedRepCount === 1 ? '' : 's'} will receive handoff emails
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center text-gray-500">
                    <Mail className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm mb-4">No email config set for {storeLabel(selectedStoreId)}.</p>
                    <button
                      onClick={handleOpenEdit}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Config
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add / Edit dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-lg shadow-lg rounded-xl bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedConfig ? 'Edit Email Config' : 'Add Email Config'}
                <span className="block text-xs font-normal text-gray-500 mt-0.5">
                  {storeLabel(selectedStoreId)}
                </span>
              </h3>
              <button
                type="button"
                onClick={handleCloseDialog}
                disabled={submitting}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {error && (
              <div ref={errorRef} role="alert" className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm mb-4 flex">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} noValidate>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Provider</label>
                <select
                  value={preset}
                  onChange={(e) => applyPreset(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  disabled={submitting}
                >
                  {Object.entries(SMTP_PRESETS).map(([key, p]) => (
                    <option key={key} value={key}>{p.label}</option>
                  ))}
                </select>
                {preset === 'gmail' && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Gmail requires an app password (not your account password) when 2FA is on.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={formData.host}
                    onChange={(e) => { setFormData({ ...formData, host: e.target.value }); setPreset('custom'); }}
                    className={inputClass('host')}
                    placeholder="smtp.example.com"
                    disabled={submitting}
                  />
                  {formErrors.host && <p className="mt-1 text-xs text-red-600">{formErrors.host}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
                  <input
                    type="number"
                    value={formData.port}
                    onChange={(e) => setFormData({ ...formData, port: e.target.value ? Number(e.target.value) : '' })}
                    className={inputClass('port')}
                    disabled={submitting}
                  />
                  {formErrors.port && <p className="mt-1 text-xs text-red-600">{formErrors.port}</p>}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Security</label>
                <div className="flex space-x-3">
                  {[
                    { value: false, label: 'STARTTLS', hint: 'port 587' },
                    { value: true, label: 'SSL/TLS', hint: 'port 465' },
                  ].map((opt) => (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => handleSecurityChange(opt.value)}
                      disabled={submitting}
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        formData.useSsl === opt.value
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label} <span className="text-xs font-normal text-gray-400">({opt.hint})</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className={inputClass('username')}
                  placeholder="user@example.com"
                  autoComplete="off"
                  disabled={submitting}
                />
                {formErrors.username && <p className="mt-1 text-xs text-red-600">{formErrors.username}</p>}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`${inputClass('password')} pr-10`}
                    placeholder={selectedConfig ? 'Leave blank to keep current password' : ''}
                    autoComplete="new-password"
                    disabled={submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute inset-y-0 right-0 px-3 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {formErrors.password && <p className="mt-1 text-xs text-red-600">{formErrors.password}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Name <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={formData.fromName}
                    onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                    className={inputClass('fromName')}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">From Email <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    type="email"
                    value={formData.fromEmail}
                    onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                    className={inputClass('fromEmail')}
                    disabled={submitting}
                  />
                  {formErrors.fromEmail && <p className="mt-1 text-xs text-red-600">{formErrors.fromEmail}</p>}
                  {!formErrors.fromEmail && (
                    <p className="mt-1 text-xs text-gray-500">
                      Required if your SMTP username isn't itself a valid email address (e.g. Mailtrap or SendGrid API-key style credentials) — otherwise outgoing emails will show a broken "From" address.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  disabled={submitting}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-70 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {selectedConfig ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Test Email dialog */}
      {testEmailDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-md shadow-lg rounded-xl bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Send Test Email
                <span className="block text-xs font-normal text-gray-500 mt-0.5">
                  {storeLabel(selectedStoreId)}
                </span>
              </h3>
              <button
                type="button"
                onClick={() => { setTestEmailDialog(false); setTestEmailError(''); }}
                disabled={sendingTestEmail}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            {testEmailError && (
              <div role="alert" className="rounded-xl bg-red-50 border border-red-200 p-4 shadow-sm mb-4 flex">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="ml-3 text-sm font-medium text-red-800">{testEmailError}</p>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Email</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                placeholder="test@example.com"
                disabled={sendingTestEmail}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => { setTestEmailDialog(false); setTestEmailError(''); }}
                disabled={sendingTestEmail}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-70 flex items-center"
              >
                {sendingTestEmail ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Test Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default EmailConfigManagement;
